import {createFileRoute} from "@tanstack/react-router";
import {getDataSourcesForFrontend} from "~/domainModels/dag/dataSources";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent, type FilterTabs} from "./-routeComponent";
import {type FilterConfiguration} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottosync/_ottosyncScaffold/data-sources/index/-filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {throwRedirectToConfigDataConnectionsWhenPresent} from "~/workspaceConfig/configOttosyncRedirects";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottosync/_ottosyncScaffold/data-sources/",
)({
    beforeLoad: ({context}) => {
        throwRedirectToConfigDataConnectionsWhenPresent(context.workspace.id);
    },
    loader: async ({context}) => {
        const dataSourcesResult = await getDataSourcesForFrontend({
            accessToken: context.authenticationStatus.accessToken,
            intellsysBaseUrl: context.intellsysBaseUrl,
            workspaceId: context.workspace.id,
        });
        if (dataSourcesResult.success !== true) {
            throw dataSourcesResult.error;
        }
        const dataSources = dataSourcesResult.data;

        const allTags = dataSources.map((d) => d.tags).flat();
        const categoryTagsSet = new Set(allTags.filter((tag) => tag.startsWith("category:")));
        const integrationTagsSet = new Set(allTags.filter((tag) => tag.startsWith("integration:")));
        const platformTagsSet = new Set(allTags.filter((tag) => tag.startsWith("source-platform:")));

        const tabsData: Record<FilterTabs, {id: Uuid; label: string; filterConfiguration: FilterConfiguration}> = {
            recommended: {
                id: "1967365f-0a2d-4c51-96dc-fee17578ee30" as Uuid,
                label: "Recommended",
                filterConfiguration: {
                    revisionId: "16942b04-7e86-4a99-bc6e-94941bd6c5f4" as Uuid,
                    filters: [
                        {
                            attribute: "displayName",
                            operation: FilterOperation.contains,
                            value: "",
                        },
                        {
                            attribute: "integration",
                            operation: FilterOperation.in,
                            values: ["integration:on-click"],
                        },
                        {
                            attribute: "category",
                            operation: FilterOperation.in,
                            values: [],
                        },
                        {
                            attribute: "platform",
                            operation: FilterOperation.in,
                            values: [],
                        },
                    ],
                },
            },
            marketing: {
                id: "1d6486e0-4e61-433d-a313-0aa7f8be3f16" as Uuid,
                label: "Marketing",
                filterConfiguration: {
                    revisionId: "16942b04-7e86-4a99-bc6e-94941bd6c5f4" as Uuid,
                    filters: [
                        {
                            attribute: "displayName",
                            operation: FilterOperation.contains,
                            value: "",
                        },
                        {
                            attribute: "integration",
                            operation: FilterOperation.in,
                            values: [],
                        },
                        {
                            attribute: "category",
                            operation: FilterOperation.in,
                            values: ["category:marketing-advertising"],
                        },
                        {
                            attribute: "platform",
                            operation: FilterOperation.in,
                            values: [],
                        },
                    ],
                },
            },
            ecommerce: {
                id: "4d451a47-d2d7-4046-b29f-8da81501d91f" as Uuid,
                label: "Ecommerce",
                filterConfiguration: {
                    revisionId: "16942b04-7e86-4a99-bc6e-94941bd6c5f4" as Uuid,
                    filters: [
                        {
                            attribute: "displayName",
                            operation: FilterOperation.contains,
                            value: "",
                        },
                        {
                            attribute: "integration",
                            operation: FilterOperation.in,
                            values: [],
                        },
                        {
                            attribute: "category",
                            operation: FilterOperation.in,
                            values: ["category:ecommerce"],
                        },
                        {
                            attribute: "platform",
                            operation: FilterOperation.in,
                            values: [],
                        },
                    ],
                },
            },
            "social-media": {
                id: "6eca3e2e-2380-4964-8454-dc9906d504d1" as Uuid,
                label: "Social Media",
                filterConfiguration: {
                    revisionId: "16942b04-7e86-4a99-bc6e-94941bd6c5f4" as Uuid,
                    filters: [
                        {
                            attribute: "displayName",
                            operation: FilterOperation.contains,
                            value: "",
                        },
                        {
                            attribute: "integration",
                            operation: FilterOperation.in,
                            values: [],
                        },
                        {
                            attribute: "category",
                            operation: FilterOperation.in,
                            values: ["category:social-media"],
                        },
                        {
                            attribute: "platform",
                            operation: FilterOperation.in,
                            values: [],
                        },
                    ],
                },
            },
            search: {
                id: "8ef9f7c8-83bf-4a15-a98c-3c906a511612" as Uuid,
                label: "Search",
                filterConfiguration: {
                    revisionId: "cd35f340-8168-4a84-8b93-ebd3c5a68674" as Uuid,
                    filters: [
                        {
                            attribute: "displayName",
                            operation: FilterOperation.contains,
                            value: "",
                        },
                        {
                            attribute: "integration",
                            operation: FilterOperation.in,
                            values: [],
                        },
                        {
                            attribute: "category",
                            operation: FilterOperation.in,
                            values: [],
                        },
                        {
                            attribute: "platform",
                            operation: FilterOperation.in,
                            values: [],
                        },
                    ],
                },
            },
        };

        return {
            dataSources: dataSources,
            allCategoryTags: Array.from(categoryTagsSet),
            allIntegrationTags: Array.from(integrationTagsSet),
            allPlatformTags: Array.from(platformTagsSet),
            tabsData: tabsData,
            //
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
